import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CardSetTransformationService, CoreService, DataService, DialogService, FormConfigurationService, SaveFormEvent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { NAV_TAB_NUMBERS, PAGE_INDEX, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, SAVE_RESULT_STATUS, SECTION_CODES, SECTION_INDEX, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { GLStringLookupMappingSaveDataRequest } from '../../interfaces/gl-string-lookup-mapping-save-request.interface';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-gl-string-lookup-mapping',
  templateUrl: './gl-string-lookup-mapping.component.html',
  styleUrl: './gl-string-lookup-mapping.component.scss'
})
export class GLStringLookupMappingComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;

  templateId: number = TEMPLATES.glStringLookupMapping;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';
  isEditing: boolean = false;

  private setId: string = ''; // The id of the parent GL String Segment Lookup Mapping Set.
  private routePrefix = '';
  private saveFormEvent: SaveFormEvent;

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private errorService: ErrorService,
    private formConfigurationService: FormConfigurationService,
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.coreService.applicationFunctions = {};
    this.coreService.conditionFunctions = {
      isEditing: () => this.isEditing
    };

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const setId = paramMap.get('setId');
      const id = paramMap.get('id');

      if (setId) {
        this.setId = setId;
      }

      if (id) {
        this.id = id;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getGlStringLookupMappingConfigAndResponse;
        this.isEditing = true;
      }
      else {
        // Get config for blank form.
        this.id = setId;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getGlStringLookupMappingConfig;
      }
    });

    // We get the first part of the URL so that we can navigate back to that route after the Cancel or Save button is clicked.
    let index = this.router.url.indexOf(`/${ROUTE_CONSTANTS.glStringLookupMapping.prefix}`);
    this.routePrefix = this.router.url.substring(0, index);
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any): void {
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.appDataService
        .post(
          API_ENDPOINTS.glStringLookupMapping.checkForOverlaps,
          {
            glStringSegmentLookupMappingId: this.id,
            gLStringSegmentLookupMappingSetId: this.setId,
            referenceData1Id: this.getReferenceData1Id(),
            startDate: this.getStartDate(),
            endDate: this.getEndDate()
          }
        )
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.glStringMappingOverlapCheck.includes(data.message)) {
              this.activeDialog = data.message;
              this.saveFormEvent = event;
              this.dialogService.showDialog(this.activeDialog);
            }
            else {
              this.completeSave(event);
            }
          }
        })
    );
  }

  cancel(event: any) {
    this.exit();
  }

  dialogButton_confirmOverlappingMapping_Clicked(event: any) {
    this.completeSave(this.saveFormEvent);
  }

  dialogButton_saveSuccessful_Clicked(event: any) {
    this.exit(true);
  }

  private exit(ignoreUnsavedChanges = false): void {
    this.navigate([this.routePrefix], ignoreUnsavedChanges);
  }

  private completeSave(event: SaveFormEvent) {
    const saveDataRequest: GLStringLookupMappingSaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.isEditing ? this.id : '',
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
      glStringLookupMappingSetId: this.setId
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  private saveData(request: GLStringLookupMappingSaveDataRequest) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.glStringLookupMapping.save, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.saveGlStringLookupMapping.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          }
        })
    );
  }

  private getReferenceData1Id(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringLookupMappingForm.glStringLookupMapping,
      'page' + PAGE_NUMBERS.glStringLookupMappingForm.glStringLookupMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringLookupMappingForm.lookupMappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringLookupMappingForm.referenceData1Id
    );
  }

  private getStartDate(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringLookupMappingForm.glStringLookupMapping,
      'page' + PAGE_NUMBERS.glStringLookupMappingForm.glStringLookupMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringLookupMappingForm.lookupMappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringLookupMappingForm.startDate
    );
  }

  private getEndDate(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringLookupMappingForm.glStringLookupMapping,
      'page' + PAGE_NUMBERS.glStringLookupMappingForm.glStringLookupMapping,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringLookupMappingForm.lookupMappingSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringLookupMappingForm.endDate
    );
  }
}
